import type {
  FmOperationalRenderFailureDescriptor,
  FmOperationalWorkspaceDescriptor,
} from "./FmOperationalWorkspaceRoot.types";
import { validateGuidance } from "./FmOperationalGuidance.validation";
import { assertFmFormFlowPageDescriptor } from "./FmFormFlowPage.validation";
import {
  validateOperationalRegions,
  type OperationalRegionValidationScope,
} from "./FmOperationalWorkspaceRegion.validation";
import {
  validateBanners,
  validateControls,
  validateMutation,
  validateOperationalDialogs,
  validateRevision,
  validateSelection,
} from "./FmOperationalWorkspaceDialog.validation";
import { validatePaidBoundaries } from "./FmOperationalPaidBoundary.validation";
import {
  validateStageOwnership,
  validateStages,
} from "./FmOperationalStage.validation";
import {
  requireResolvingControl,
  validateSelectionScope,
  validateStateSurface,
  validateStatusTruthfulness,
} from "./FmOperationalWorkspaceState.validation";
import {
  exactOperationalKeys as exact,
  failOperationalDescriptor as fail,
  operationalArray as array,
  operationalObject as object,
  operationalOneOf as oneOf,
  operationalText as text,
  optionalOperationalText as optionalText,
} from "./FmOperationalWorkspaceValidation.shared";

const WORKSPACE_STATES = [
  "ready",
  "loading",
  "empty",
  "stale",
  "blocked",
  "error",
] as const;

/** The typed surface key each non-ready state must carry, and only it. */
const STATE_SURFACE_KEY: Readonly<Record<string, string>> = {
  empty: "empty",
  stale: "stale",
  blocked: "blocked",
  error: "error",
};

const BASE_ROOT_KEYS = [
  "id",
  "ariaLabel",
  "title",
  "description",
  "masthead",
  "returnAction",
  "vocabulary",
  "state",
  "stateLabel",
  "activeStageId",
  "stages",
  "guidance",
  "revision",
  "selection",
  "mutation",
  "banners",
  "filters",
  "controls",
  "regions",
  "paidBoundaries",
  "dialogs",
  "formFlow",
  "renderFailure",
  "routePending",
  "emptyLabel",
  "onEvent",
] as const;

function validateReturnAction(value: unknown): void {
  if (value === undefined) return;
  const action = object(value, "descriptor.returnAction");
  exact(
    action,
    new Set(["label", "onActivate"]),
    "descriptor.returnAction",
  );
  text(action.label, "descriptor.returnAction.label");
  if (typeof action.onActivate !== "function") {
    fail("descriptor.returnAction.onActivate", "must be a function");
  }
}

function validateFormFlow(value: unknown): void {
  if (value === undefined) return;
  const formFlow = object(value, "descriptor.formFlow");
  exact(
    formFlow,
    new Set(["id", "open", "title", "closeLabel", "size", "flow"]),
    "descriptor.formFlow",
  );
  ["id", "title", "closeLabel"].forEach((key) =>
    text(formFlow[key], `descriptor.formFlow.${key}`),
  );
  if (typeof formFlow.open !== "boolean")
    fail("descriptor.formFlow.open", "must be boolean");
  oneOf(formFlow.size, ["md", "lg"], "descriptor.formFlow.size");
  assertFmFormFlowPageDescriptor(formFlow.flow);
}

/**
 * The route's failure surface, read on its own before the rest of the contract:
 * the root renders it as the boundary AROUND the shell check, so a shell
 * violation shows this surface instead of unmounting the route. A declared
 * `renderFailure` wins; otherwise the vocabulary's required `routeFailure` copy.
 */
export function assertFmOperationalRenderFailure(
  descriptorValue: unknown,
): FmOperationalRenderFailureDescriptor {
  const descriptor = object(descriptorValue, "descriptor");
  validateRenderFailure(descriptor.renderFailure);
  if (descriptor.renderFailure !== undefined)
    return descriptor.renderFailure as FmOperationalRenderFailureDescriptor;
  const vocabulary = object(descriptor.vocabulary, "descriptor.vocabulary");
  const copy = validateRouteFailure(vocabulary.routeFailure);
  const id = text(descriptor.id, "descriptor.id");
  return { ...copy, resetKey: id, testId: `${id}-render-error` };
}

function validateRouteFailure(
  value: unknown,
): Omit<FmOperationalRenderFailureDescriptor, "resetKey" | "testId"> {
  const path = "descriptor.vocabulary.routeFailure";
  const failure = object(value, path);
  exact(failure, new Set(["title", "description", "retryLabel"]), path);
  return {
    title: text(failure.title, `${path}.title`),
    description: text(failure.description, `${path}.description`),
    retryLabel: text(failure.retryLabel, `${path}.retryLabel`),
  };
}

function validateRenderFailure(value: unknown): void {
  if (value === undefined) return;
  const failure = object(value, "descriptor.renderFailure");
  exact(
    failure,
    new Set(["title", "description", "retryLabel", "resetKey", "testId"]),
    "descriptor.renderFailure",
  );
  ["title", "description", "retryLabel", "resetKey"].forEach((key) =>
    text(failure[key], `descriptor.renderFailure.${key}`),
  );
  if (failure.testId !== undefined)
    text(failure.testId, "descriptor.renderFailure.testId");
}

function rootKeysFor(state: string): ReadonlySet<string> {
  const surfaceKey = STATE_SURFACE_KEY[state];
  return new Set(surfaceKey ? [...BASE_ROOT_KEYS, surfaceKey] : BASE_ROOT_KEYS);
}

const MEDIA_SCOPES = ["editorial", "property", "stand"] as const;
const MEDIA_SPECIES = [
  "pine",
  "spruce",
  "birch",
  "deciduous",
  "mixed",
  "other",
  "unknown",
] as const;

function validateMasthead(value: unknown): void {
  if (value === undefined) return;
  const masthead = object(value, "descriptor.masthead");
  exact(masthead, new Set(["mediaContext"]), "descriptor.masthead");
  const media = object(
    masthead.mediaContext,
    "descriptor.masthead.mediaContext",
  );
  exact(
    media,
    new Set([
      "scope",
      "species",
      "developmentClass",
      "landClass",
      "structure",
      "condition",
      "reportedCause",
      "basisLabel",
      "sourceRevision",
      "freshness",
      "actualMedia",
      "allowIllustrativeFallback",
      "stateId",
    ]),
    "descriptor.masthead.mediaContext",
  );
  oneOf(
    media.scope,
    [...MEDIA_SCOPES],
    "descriptor.masthead.mediaContext.scope",
  );
  if (media.species !== undefined) {
    oneOf(
      media.species,
      [...MEDIA_SPECIES],
      "descriptor.masthead.mediaContext.species",
    );
  }
  if (typeof media.allowIllustrativeFallback !== "boolean") {
    fail(
      "descriptor.masthead.mediaContext.allowIllustrativeFallback",
      "must be a boolean",
    );
  }
  optionalText(media.basisLabel, "descriptor.masthead.mediaContext.basisLabel");
}

function validateVocabulary(value: unknown): void {
  const vocabulary = object(value, "descriptor.vocabulary");
  exact(
    vocabulary,
    new Set([
      "workspace",
      "dataRevision",
      "status",
      "statuses",
      "rowActions",
      "regionFailure",
      "routeFailure",
    ]),
    "descriptor.vocabulary",
  );
  ["workspace", "dataRevision", "status", "rowActions"].forEach((key) =>
    text(vocabulary[key], `descriptor.vocabulary.${key}`),
  );
  const regionFailure = object(
    vocabulary.regionFailure,
    "descriptor.vocabulary.regionFailure",
  );
  exact(
    regionFailure,
    new Set(["title", "description"]),
    "descriptor.vocabulary.regionFailure",
  );
  ["title", "description"].forEach((key) =>
    text(regionFailure[key], `descriptor.vocabulary.regionFailure.${key}`),
  );
  validateRouteFailure(vocabulary.routeFailure);
  const statuses = object(
    vocabulary.statuses,
    "descriptor.vocabulary.statuses",
  );
  exact(statuses, new Set(WORKSPACE_STATES), "descriptor.vocabulary.statuses");
  WORKSPACE_STATES.forEach((key) =>
    text(statuses[key], `descriptor.vocabulary.statuses.${key}`),
  );
}

function validateFilters(value: unknown): void {
  const filters = array(value, "descriptor.filters");
  const filterIds = new Set<string>();
  filters.forEach((filterValue, filterIndex) => {
    const path = `descriptor.filters[${filterIndex}]`;
    const filter = object(filterValue, path);
    if (filter.kind !== "select") fail(`${path}.kind`, "is not registered");
    exact(
      filter,
      new Set(["kind", "id", "label", "value", "options", "searchable"]),
      path,
    );
    filterIds.add(text(filter.id, `${path}.id`));
    text(filter.label, `${path}.label`);
    text(filter.value, `${path}.value`);
    if (
      filter.searchable !== undefined &&
      typeof filter.searchable !== "boolean"
    ) {
      fail(`${path}.searchable`, "must be boolean");
    }
    const optionValues = new Set<string>();
    array(filter.options, `${path}.options`).forEach(
      (optionValue, optionIndex) => {
        const optionPath = `${path}.options[${optionIndex}]`;
        const option = object(optionValue, optionPath);
        exact(option, new Set(["value", "label", "disabled"]), optionPath);
        optionValues.add(text(option.value, `${optionPath}.value`));
        text(option.label, `${optionPath}.label`);
        if (
          option.disabled !== undefined &&
          typeof option.disabled !== "boolean"
        ) {
          fail(`${optionPath}.disabled`, "must be boolean");
        }
      },
    );
    if (optionValues.size !== array(filter.options, `${path}.options`).length) {
      fail(`${path}.options`, "must use unique values");
    }
    if (!optionValues.has(String(filter.value))) {
      fail(`${path}.value`, "must reference a declared option");
    }
  });
  if (filterIds.size !== filters.length)
    fail("descriptor.filters", "must use unique ids");
}

/** The whole contract, every region's section included. */
export function assertFmOperationalWorkspaceDescriptor(
  descriptorValue: unknown,
): asserts descriptorValue is FmOperationalWorkspaceDescriptor {
  assertWorkspace(descriptorValue, "whole");
}

/**
 * The contract minus each region's section, which the live root leaves to the
 * rendered region's error boundary so a failure stays inside that region.
 */
export function assertFmOperationalWorkspaceShell(
  descriptorValue: unknown,
): asserts descriptorValue is FmOperationalWorkspaceDescriptor {
  assertWorkspace(descriptorValue, "shell");
}

function assertWorkspace(
  descriptorValue: unknown,
  regionScope: OperationalRegionValidationScope,
): asserts descriptorValue is FmOperationalWorkspaceDescriptor {
  const descriptor = object(descriptorValue, "descriptor");
  const state = oneOf(
    descriptor.state,
    [...WORKSPACE_STATES],
    "descriptor.state",
  );
  exact(descriptor, rootKeysFor(state), "descriptor");
  [
    "id",
    "ariaLabel",
    "title",
    "stateLabel",
    "activeStageId",
    "emptyLabel",
  ].forEach((key) => text(descriptor[key], `descriptor.${key}`));
  optionalText(descriptor.description, "descriptor.description");
  validateMasthead(descriptor.masthead);
  validateReturnAction(descriptor.returnAction);
  if (
    descriptor.onEvent !== undefined &&
    typeof descriptor.onEvent !== "function"
  )
    fail("descriptor.onEvent", "must be a typed event handler");
  validateVocabulary(descriptor.vocabulary);
  const revisionId = validateRevision(descriptor.revision);
  validateSelection(descriptor.selection, revisionId);
  validateStages(descriptor.stages, descriptor.activeStageId);
  validateGuidance(
    descriptor.guidance,
    descriptor.stages,
    descriptor.masthead,
  );
  validateMutation(descriptor.mutation);
  validateBanners(descriptor.banners);
  validateFilters(descriptor.filters);
  const controls = validateControls(descriptor.controls, revisionId);
  const controlIds = new Set(controls.map((control) => String(control.id)));
  const disabledControlIds = new Set(
    controls
      .filter((control) => control.disabled === true)
      .map((control) => String(control.id)),
  );
  validateOperationalRegions(
    descriptor.regions,
    controlIds,
    disabledControlIds,
    regionScope,
  );
  validateStageOwnership(descriptor.stages, controls, descriptor.regions);
  validateOperationalDialogs(descriptor.dialogs, revisionId, controls);
  validateFormFlow(descriptor.formFlow);
  validateRenderFailure(descriptor.renderFailure);
  if (
    descriptor.routePending !== undefined &&
    typeof descriptor.routePending !== "boolean"
  ) {
    fail("descriptor.routePending", "must be boolean");
  }
  const stageIds = new Set(
    array(descriptor.stages, "descriptor.stages").map((item, index) =>
      String(object(item, `descriptor.stages[${index}]`).id),
    ),
  );
  const regionIds = new Set(
    array(descriptor.regions, "descriptor.regions").map((item, index) =>
      String(object(item, `descriptor.regions[${index}]`).id),
    ),
  );
  validateStateSurface(descriptor, controls, stageIds, regionIds);
  validatePaidBoundaries(
    descriptor.paidBoundaries,
    controls,
    stageIds,
    regionIds,
  );
  validateStatusTruthfulness(descriptor);
  validateSelectionScope(descriptor.selection, descriptor.regions);
  array(
    object(descriptor.mutation, "descriptor.mutation").receipts,
    "descriptor.mutation.receipts",
  ).forEach((item, index) => {
    const receipt = object(item, `descriptor.mutation.receipts[${index}]`);
    if (receipt.kind !== "blocker") return;
    requireResolvingControl(
      receipt.resolvingActionId,
      `descriptor.mutation.receipts[${index}].resolvingActionId`,
      controls,
    );
  });
}
